import {
  type ComponentVariant,
  type ComponentSize,
} from 'src/modules/shared/types/component.type';
import {
  resolveVariantClasses,
  variantTokens,
} from 'src/modules/shared/styles/variant-classes';

export const baseButtonClasses: string[] = [
  'inline-flex',
  'items-center',
  'justify-center',
  'gap-2',
  'whitespace-nowrap',
  'font-medium',
  'transition-all',
  'duration-200',
  'outline-none',
  'focus:outline-none',
  'focus-visible:ring-2',
  'focus-visible:ring-primary-500',
  'focus-visible:ring-offset-2',
  'disabled:opacity-50',
  'disabled:cursor-not-allowed',
];

export const buttonFilledClasses: Record<
  NonNullable<ComponentVariant>,
  string
> = {
  primary: `${variantTokens.primary.solid} hover:bg-primary-400 active:bg-primary-300 disabled:bg-primary-500 disabled:hover:bg-primary-500`,
  secondary: `${variantTokens.secondary.solid} hover:bg-secondary-400 active:bg-secondary-300 disabled:bg-secondary-500 disabled:hover:bg-secondary-500`,
  danger: `${variantTokens.danger.solid} hover:bg-danger-400 active:bg-danger-300 disabled:bg-danger-500 disabled:hover:bg-danger-500`,
  warning: `${variantTokens.warning.solid} hover:bg-warning-400 active:bg-warning-300 disabled:bg-warning-500 disabled:hover:bg-warning-500`,
  neutral: `${variantTokens.neutral.solid} hover:bg-neutral-400 active:bg-neutral-300 disabled:bg-neutral-500 disabled:hover:bg-neutral-500`,
  success: `${variantTokens.success.solid} hover:bg-success-400 active:bg-success-300 disabled:bg-success-500 disabled:hover:bg-success-500`,
  info: `${variantTokens.info.solid} hover:bg-blue-400 active:bg-blue-300 disabled:bg-blue-500 disabled:hover:bg-blue-500`,
  whatsapp: `${variantTokens.whatsapp.solid} hover:bg-whatsapp-400 active:bg-whatsapp-300 disabled:bg-whatsapp-500 disabled:hover:bg-whatsapp-500`,
  call: `${variantTokens.call.solid} hover:bg-call-400 active:bg-call-300 disabled:bg-call-500 disabled:hover:bg-call-500`,
  email: `${variantTokens.email.solid} hover:bg-email-400 active:bg-email-300 disabled:bg-email-500 disabled:hover:bg-email-500`,
};

export const buttonOutlineClasses: Record<
  NonNullable<ComponentVariant>,
  string
> = {
  primary: `${resolveVariantClasses('primary', 'outline')} hover:bg-primary-50 active:bg-primary-100 disabled:bg-white disabled:hover:bg-white`,
  secondary: `${resolveVariantClasses('secondary', 'outline')} hover:bg-secondary-50 active:bg-secondary-100 disabled:bg-white disabled:hover:bg-white`,
  danger: `${resolveVariantClasses('danger', 'outline')} hover:bg-danger-50 active:bg-danger-100 disabled:bg-white disabled:hover:bg-white`,
  warning: `${resolveVariantClasses('warning', 'outline')} hover:bg-warning-50 active:bg-warning-100 disabled:bg-white disabled:hover:bg-white`,
  neutral: `${resolveVariantClasses('neutral', 'outline')} hover:bg-neutral-50 active:bg-neutral-100 disabled:bg-white disabled:hover:bg-white`,
  success: `${resolveVariantClasses('success', 'outline')} hover:bg-success-50 active:bg-success-100 disabled:bg-white disabled:hover:bg-white`,
  info: `${resolveVariantClasses('info', 'outline')} hover:bg-blue-50 active:bg-blue-100 disabled:bg-white disabled:hover:bg-white`,
  whatsapp: `${resolveVariantClasses('whatsapp', 'outline')} hover:bg-whatsapp-50 active:bg-whatsapp-100 disabled:bg-white disabled:hover:bg-white`,
  call: `${resolveVariantClasses('call', 'outline')} hover:bg-call-50 active:bg-call-100 disabled:bg-white disabled:hover:bg-white`,
  email: `${resolveVariantClasses('email', 'outline')} hover:bg-email-50 active:bg-email-100 disabled:bg-white disabled:hover:bg-white`,
};

export const buttonSoftClasses: Record<
  NonNullable<ComponentVariant>,
  string
> = {
  primary: `${resolveVariantClasses('primary', 'soft')} hover:bg-primary-100 active:bg-primary-200 disabled:bg-primary-50 disabled:hover:bg-primary-50`,
  secondary: `${resolveVariantClasses('secondary', 'soft')} hover:bg-secondary-100 active:bg-secondary-200 disabled:bg-secondary-50 disabled:hover:bg-secondary-50`,
  danger: `${resolveVariantClasses('danger', 'soft')} hover:bg-danger-100 active:bg-danger-200 disabled:bg-danger-50 disabled:hover:bg-danger-50`,
  warning: `${resolveVariantClasses('warning', 'soft')} hover:bg-warning-100 active:bg-warning-200 disabled:bg-warning-50 disabled:hover:bg-warning-50`,
  neutral: `${resolveVariantClasses('neutral', 'soft')} hover:bg-neutral-100 active:bg-neutral-200 disabled:bg-neutral-50 disabled:hover:bg-neutral-50`,
  success: `${resolveVariantClasses('success', 'soft')} hover:bg-success-100 active:bg-success-200 disabled:bg-success-50 disabled:hover:bg-success-50`,
  info: `${resolveVariantClasses('info', 'soft')} hover:bg-blue-100 active:bg-blue-200 disabled:bg-blue-50 disabled:hover:bg-blue-50`,
  whatsapp: `${resolveVariantClasses('whatsapp', 'soft')} hover:bg-whatsapp-100 active:bg-whatsapp-200 disabled:bg-whatsapp-50 disabled:hover:bg-whatsapp-50`,
  call: `${resolveVariantClasses('call', 'soft')} hover:bg-call-100 active:bg-call-200 disabled:bg-call-50 disabled:hover:bg-call-50`,
  email: `${resolveVariantClasses('email', 'soft')} hover:bg-email-100 active:bg-email-200 disabled:bg-email-50 disabled:hover:bg-email-50`,
};

export const buttonGhostClasses: Record<
  NonNullable<ComponentVariant>,
  string
> = {
  primary: `${resolveVariantClasses('primary', 'ghost')} hover:bg-primary-50 active:bg-primary-100`,
  secondary: `${resolveVariantClasses('secondary', 'ghost')} hover:bg-secondary-50 active:bg-secondary-100`,
  danger: `${resolveVariantClasses('danger', 'ghost')} hover:bg-danger-50 active:bg-danger-100`,
  warning: `${resolveVariantClasses('warning', 'ghost')} hover:bg-warning-50 active:bg-warning-100`,
  neutral: `${resolveVariantClasses('neutral', 'ghost')} hover:bg-neutral-50 active:bg-neutral-100`,
  success: `${resolveVariantClasses('success', 'ghost')} hover:bg-success-50 active:bg-success-100`,
  info: `${resolveVariantClasses('info', 'ghost')} hover:bg-blue-50 active:bg-blue-100`,
  whatsapp: `${resolveVariantClasses('whatsapp', 'ghost')} hover:bg-whatsapp-50 active:bg-whatsapp-100`,
  call: `${resolveVariantClasses('call', 'ghost')} hover:bg-call-50 active:bg-call-100`,
  email: `${resolveVariantClasses('email', 'ghost')} hover:bg-email-50 active:bg-email-100`,
};

/**
 * Maps input size to appropriate button size for inline inputs.
 * Buttons inside inputs should be one size smaller to fit properly.
 */
export const getInlineButtonSize = (
  inputSize: ComponentSize
): ComponentSize => {
  switch (inputSize) {
    case '2xs':
      return '2xs';
    case 'xs':
      return '2xs';
    case 'sm':
      return 'xs';
    case 'md':
      return 'sm';
    case 'lg':
      return 'md';
    default:
      return 'sm';
  }
};

/**
 * Maps component size to appropriate icon classes for inline inputs.
 * Returns both text size and explicit width/height for consistent icon sizing.
 */
export const getInlineIconSize = (size: ComponentSize): string => {
  switch (size) {
    case '2xs':
      return 'text-sm h-3 w-3';
    case 'xs':
      return 'text-sm h-3.5 w-3.5';
    case 'sm':
      return 'text-lg h-4 w-4';
    case 'md':
      return 'text-xl h-5 w-5';
    case 'lg':
      return 'text-2xl h-6 w-6';
    default:
      return 'text-xl h-5 w-5';
  }
};
/**
 * Gets padding for inline inputs to accommodate action buttons.
 * Scales padding based on component size to ensure buttons fit properly.
 * @param size - Component size
 * @param extraPadding - Optional additional padding in rem to add
 * @returns Padding value in rem
 */
export const getInlineInputRemPadding = (
  size: ComponentSize,
  extraPadding = 0
): number => {
  let basePadding: number;
  switch (size) {
    case '2xs':
      basePadding = 2;
      break;
    case 'xs':
      basePadding = 2.5;
      break;
    case 'sm':
      basePadding = 3.5;
      break;
    case 'md':
      basePadding = 4;
      break;
    case 'lg':
      basePadding = 5;
      break;
    default:
      basePadding = 4;
  }

  return basePadding + extraPadding;
};
